export interface SLAWeights {
  category: string;
  weight: number;
  key: string;
}

export interface ScoreBand {
  range: string;
  min: number;
  max: number;
  label: string;
  color: string;
  bgClass: string;
  textClass: string;
  recommendation: string;
  action: string;
}

export interface ScoreDescription {
  score: number;
  label: string;
  descriptions: Record<string, string>;
}

export const slaWeights: SLAWeights[] = [
  { category: "Quality of Service", weight: 30, key: "quality" },
  { category: "Timeliness & Delivery", weight: 25, key: "timeliness" },
  { category: "Value for Money", weight: 20, key: "value" },
  { category: "Communication", weight: 15, key: "communication" },
  { category: "Compliance", weight: 10, key: "compliance" },
];

export const scoreBands: ScoreBand[] = [
  {
    range: "4.0 – 5.0",
    min: 4.0,
    max: 5.0,
    label: "Excellent",
    color: "#166534",
    bgClass: "bg-secondary-500",
    textClass: "text-secondary-700",
    recommendation: "Auto-Renew",
    action: "Approve contract renewal without conditions. Supplier consistently exceeds expectations.",
  },
  {
    range: "3.0 – 3.9",
    min: 3.0,
    max: 3.9,
    label: "Satisfactory",
    color: "#1D4ED8",
    bgClass: "bg-primary-500",
    textClass: "text-primary-700",
    recommendation: "Conditional Renewal",
    action: "Renew with standard performance monitoring. Address any minor concerns in the next review cycle.",
  },
  {
    range: "2.0 – 2.9",
    min: 2.0,
    max: 2.9,
    label: "Needs Improvement",
    color: "#C2410C",
    bgClass: "bg-accent-500",
    textClass: "text-accent-700",
    recommendation: "Conditional Renewal",
    action: "Renew only with a formal improvement plan and shortened review cycle (quarterly). Escalate to SCM Manager.",
  },
  {
    range: "0.0 – 1.9",
    min: 0.0,
    max: 1.9,
    label: "Unsatisfactory",
    color: "#7F1D1D",
    bgClass: "bg-foreground-500",
    textClass: "text-foreground-700",
    recommendation: "Re-Advertise",
    action: "Do not renew. Initiate new procurement process immediately. Flag for early termination review.",
  },
];

export const scoreDescriptions: ScoreDescription[] = [
  {
    score: 5,
    label: "Outstanding",
    descriptions: {
      quality: "Exceeds all quality standards. Work consistently defect-free. Proactive improvement suggestions.",
      timeliness: "All deliverables ahead of schedule. Zero missed deadlines. Proactive timeline management.",
      value: "Exceptional value. Delivers above-contract scope at no extra cost. Identifies cost-saving opportunities.",
      communication: "Proactive, transparent communication. Weekly status updates. Issues flagged before they become problems.",
      compliance: "Full compliance with all regulatory and contractual requirements. Audit-ready at all times.",
    },
  },
  {
    score: 4,
    label: "Good",
    descriptions: {
      quality: "Meets all quality standards. Minimal rework required. Work is reliable and consistent.",
      timeliness: "All deadlines met. Minor delays communicated in advance with recovery plans.",
      value: "Good value relative to cost. Deliverables match contracted scope. Occasional above-and-beyond effort.",
      communication: "Regular status updates. Responsive to queries within agreed timeframes. Clear escalation paths used.",
      compliance: "All mandatory compliance requirements met. Documentation complete and accessible.",
    },
  },
  {
    score: 3,
    label: "Adequate",
    descriptions: {
      quality: "Meets minimum quality standards. Some rework required. Inconsistent across different workstreams.",
      timeliness: "Most deadlines met. Occasional delays of 1-5 business days without prior notice.",
      value: "Adequate value. Delivers contracted scope but no additional effort. Pricing at market average.",
      communication: "Reactive communication. Responds when prompted but rarely proactive. Monthly updates only.",
      compliance: "Core compliance met. Some documentation gaps or delays. Requires follow-up for outstanding items.",
    },
  },
  {
    score: 2,
    label: "Below Average",
    descriptions: {
      quality: "Frequent quality issues. Significant rework on most deliverables. Customer complaints registered.",
      timeliness: "Recurring delays of 1-4 weeks. Milestone dates consistently missed. Poor schedule management.",
      value: "Below-expected value. Scope creep without justification. Cost overruns without prior approval.",
      communication: "Inconsistent communication. Delayed responses to queries. Escalation points unclear or unresponsive.",
      compliance: "Compliance gaps identified. Incomplete statutory documentation. Requires formal remediation notice.",
    },
  },
  {
    score: 1,
    label: "Poor",
    descriptions: {
      quality: "Unacceptable quality. Work product unusable or requires complete redo. Formal quality complaints filed.",
      timeliness: "Critical deadlines missed by 4+ weeks. Project milestones jeopardized. No recovery plan in place.",
      value: "Severe value deficit. Cost far exceeds benefit. Contract scope not being delivered despite full payment.",
      communication: "Unresponsive. Multiple attempts required for basic contact. No status reporting provided.",
      compliance: "Serious compliance breaches. Regulatory risk exposure. Legal or disciplinary action under consideration.",
    },
  },
];

export function calculateWeightedScore(categoryScores: Record<string, number>): number {
  let totalWeightedScore = 0;
  const categoryKeyMap: Record<string, string> = {
    "Quality of Service": "quality",
    "Timeliness & Delivery": "timeliness",
    "Value for Money": "value",
    "Communication": "communication",
    "Compliance": "compliance",
  };

  slaWeights.forEach((w) => {
    const key = categoryKeyMap[w.category];
    const score = categoryScores[key] || 0;
    totalWeightedScore += score * (w.weight / 100);
  });

  return Math.round(totalWeightedScore * 10) / 10;
}

export function getBandForScore(score: number): ScoreBand {
  for (const band of scoreBands) {
    if (score >= band.min && score <= band.max) return band;
  }
  return scoreBands[3];
}

export const demoReviewScores = {
  quality: 5,
  timeliness: 4,
  value: 3,
  communication: 4,
  compliance: 5,
};